// RasporedRow.js

import React, { useRef, useState } from 'react';
import EditScheduleModal from './EditScheduleModal';
import { db } from '../config/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { FaEdit, FaCheck, FaFileExport } from 'react-icons/fa';
import '../css/RasporedRow.css';
import { getAuth } from 'firebase/auth';
import TooltipPortal from './TooltipPortal';

function RasporedRow({
  person,
  weekSchedules = [],
  updateWeekSchedule, // Primamo funkciju
  daysOfWeek = [],
  firms = [],
  colSpan,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const nameRef = useRef(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });

  // Dohvaćamo raspored za osobu
  const personSchedule = weekSchedules.find(
    (schedule) => schedule.riderID === person.riderID
  );

  // Provjeravamo je li raspored potvrđen
  const isConfirmed = personSchedule && personSchedule.confirmed;

  // Funkcija za dohvaćanje imena firme
  const getFirmName = (firmId) => {
    const firm = firms.find((firm) => firm.id === firmId);
    return firm ? firm.name : 'N/A';
  };

  // Funkcija za spremanje promjena u rasporedu
  const handleSaveSchedule = (editedSchedule) => {
    if (personSchedule && personSchedule.id) {
      updateScheduleInFirestore(personSchedule.id, editedSchedule);
    } else {
      alert('Nema dostupnog rasporeda za ovu osobu.');
    }
  };

  // Funkcija za ažuriranje rasporeda u Firestore
  const updateScheduleInFirestore = async (scheduleId, editedSchedule) => {
    try {
      const scheduleRef = doc(db, 'weekSchedule', scheduleId);
      await updateDoc(scheduleRef, {
        schedule: editedSchedule,
        confirmed: true, // Automatski potvrđujemo nakon uređivanja
      });

      // Kreiramo ažurirani objekt rasporeda
      const updatedSchedule = {
        ...personSchedule,
        schedule: editedSchedule,
        confirmed: true,
      };

      // Ažuriramo stanje u parent komponenti
      updateWeekSchedule(updatedSchedule);

      alert('Raspored uspješno ažuriran i potvrđen.');
    } catch (error) {
      console.error('Greška pri ažuriranju rasporeda:', error);
      alert('Greška pri ažuriranju rasporeda.');
    }
  };

  // Funkcija za potvrđivanje rasporeda
  const confirmSchedule = async () => {
    if (personSchedule && personSchedule.id) {
      try {
        const scheduleRef = doc(db, 'weekSchedule', personSchedule.id);
        await updateDoc(scheduleRef, {
          confirmed: true,
        });

        // Ažuriramo lokalno stanje
        const updatedSchedule = {
          ...personSchedule,
          confirmed: true,
        };

        updateWeekSchedule(updatedSchedule);

        alert('Raspored potvrđen.');
      } catch (error) {
        console.error('Greška pri potvrđivanju rasporeda:', error);
        alert('Greška pri potvrđivanju rasporeda.');
      }
    } else {
      alert('Nema dostupnog rasporeda za ovu osobu.');
    }
  };

  // **Ažurirana funkcija za generiranje dnevnih podataka**
  const dailyData = daysOfWeek.map((day) => {
    const dateKey = day.date.toISOString().split('T')[0];

    if (personSchedule && personSchedule.schedule) {
      const dailySchedule = personSchedule.schedule[dateKey];

      if (dailySchedule) {
        // **Prilagodba logike grupiranja radnih blokova**

        // Pretvaramo vremenske slotove u minute za lakšu obradu
        const timeSlotsInMinutes = dailySchedule.map((timeSlot) => {
          const [startTime, endTime] = timeSlot.split(' - ');
          const [startHour, startMinute] = startTime.split(':').map(Number);
          const [endHour, endMinute] = endTime.split(':').map(Number);
          return {
            start: startHour * 60 + startMinute,
            end: endHour * 60 + endMinute,
            display: timeSlot,
          };
        });

        // Sortiramo vremenske slotove po početnom vremenu
        timeSlotsInMinutes.sort((a, b) => a.start - b.start);

        // Grupiramo u radne blokove
        const blocks = [];
        let currentBlock = null;

        timeSlotsInMinutes.forEach((slot) => {
          if (!currentBlock) {
            currentBlock = { start: slot.start, end: slot.end };
          } else {
            if (slot.start === currentBlock.end) {
              // Kontinuirani radni blok
              currentBlock.end = slot.end;
            } else {
              // Pauza je između, spremamo trenutni blok i započinjemo novi
              blocks.push({ ...currentBlock });
              currentBlock = { start: slot.start, end: slot.end };
            }
          }
        });

        // Ne zaboravimo dodati posljednji blok
        if (currentBlock) {
          blocks.push({ ...currentBlock });
        }

        // Ograničavamo broj blokova na 3
        if (blocks.length > 3) {
          blocks.length = 3;
        }

        // Konvertiramo blokove nazad u format vremena za prikaz
        const formattedBlocks = blocks.map((block) => {
          const formatTime = (minutes) => {
            const hour = Math.floor(minutes / 60)
              .toString()
              .padStart(2, '0');
            const minute = (minutes % 60).toString().padStart(2, '0');
            return `${hour}:${minute}`;
          };
          return `${formatTime(block.start)} - ${formatTime(block.end)}`;
        });

        // Osiguravamo da imamo 3 reda (dodajemo null ako ih je manje)
        while (formattedBlocks.length < 3) {
          formattedBlocks.push(null);
        }

        // Izračun ukupnih sati za taj dan
        const totalHours = blocks.reduce(
          (sum, block) => sum + (block.end - block.start) / 60,
          0
        );

        return {
          blocks: formattedBlocks,
          totalHours,
        };
      }
    }
    // Ako nema rasporeda za taj dan, vraćamo prazne blokove i 0 sati
    return {
      blocks: [null, null, null],
      totalHours: 0,
    };
  });

  // Izračun ukupnih sati za tjedan
  const totalWeekHours = dailyData.reduce(
    (total, dayData) => total + dayData.totalHours,
    0
  );

   // Add the handleExport function
   const handleExport = async () => {
    if (personSchedule && personSchedule.schedule) {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
  
        if (!currentUser) {
          alert('User not authenticated.');
          return;
        }
  
        const idToken = await currentUser.getIdToken();
  
        const response = await fetch('https://exportschedule-syg4jytmeq-uc.a.run.app', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            riderID: person.riderID,
            schedule: personSchedule.schedule,
          }),
        });
  
        if (response.ok) {
          alert('Raspored uspješno izvezen.');
        } else {
          const errorText = await response.text();
          alert(`Greška pri izvozu rasporeda: ${errorText}`);
        }
      } catch (error) {
        console.error('Greška pri izvozu rasporeda:', error);
        alert('Greška pri izvozu rasporeda.');
      }
    } else {
      alert('Nema dostupnog rasporeda za ovu osobu.');
    }
  };

  const handleMouseEnter = (e) => {
    if (!person.comment) return;
    const rect = e.target.getBoundingClientRect();
    setTooltip({
      visible: true,
      x: rect.right,
      y: rect.top + rect.height / 2,
      content: person.comment,
    });
  };
  const handleMouseMove = (e) => {
    if (!person.comment) return;
    const rect = e.target.getBoundingClientRect();
    setTooltip((prev) => ({ ...prev, x: rect.right, y: rect.top + rect.height / 2 }));
  };
  const handleMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  return (
    <>
      {Array.from({ length: 3 }, (_, rowIndex) => (
        <tr key={`${person.id}-row-${rowIndex}`}>
          {rowIndex === 0 && (
            <>
              <td style={{position:'relative'}}>
                <span
                  ref={nameRef}
                  onMouseEnter={handleMouseEnter}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  style={{position:'relative',display:'inline-block'}}
                >
                  {person.name}
                </span>
                <TooltipPortal
                  visible={tooltip.visible}
                  content={tooltip.content}
                  x={tooltip.x}
                  y={tooltip.y}
                  preferSide="right"
                />
              </td>
              {/* Dodajemo stupac za gumbe odmah nakon imena */}
              <td rowSpan={3}>
                <button
                  className="edit-button"
                  onClick={() => {
                    if (personSchedule && personSchedule.schedule) {
                      setIsModalOpen(true);
                    } else {
                      alert('Nema dostupnog rasporeda za ovu osobu.');
                    }
                  }}
                  title="Uredi raspored"
                >
                  <FaEdit /> Uredi
                </button>
                <button
                  className="confirm-button"
                  onClick={confirmSchedule}
                  title="Potvrdi raspored"
                >
                  <FaCheck /> Potvrdi
                </button>
                <button
                  className="confirm-button"
                  onClick={handleExport}
                  title="Izvezi raspored"
                >
                  <FaFileExport /> Izvezi
                </button>
              </td>
              <td rowSpan={3}>{person.riderID}</td>
              <td rowSpan={3}>{person.city || ''}</td>
              <td rowSpan={3}>{getFirmName(person.firmId)}</td>
              <td rowSpan={3}>{person.nationality}</td>
            </>
          )}
          {dailyData.map((dayData, dayIndex) => {
            const block = dayData.blocks[rowIndex];
            let timeCell;

            // Generiramo ćeliju za radni blok ili ostavljamo praznu ćeliju
            if (block) {
              // Određujemo klasu na temelju potvrđenosti rasporeda
              const cellClass = isConfirmed
                ? 'confirmed-cell'
                : 'unconfirmed-cell';

              timeCell = (
                <td key={`time-${dayIndex}-${rowIndex}`} className={cellClass}>
                  {block}
                </td>
              );
            } else {
              timeCell = (
                <td key={`time-${dayIndex}-${rowIndex}`}></td> // Prazna ćelija
              );
            }

            // Generiramo ćeliju za "Ukupno" samo u prvom redu za taj dan
            let totalHoursCell = null;
            if (rowIndex === 0) {
              if (dayData.totalHours > 0) {
                totalHoursCell = (
                  <td key={`total-${dayIndex}`} rowSpan={3}>
                    {dayData.totalHours} h
                  </td>
                );
              } else {
                // Ako nema sati za taj dan, prikazujemo "no schedule"
                totalHoursCell = (
                  <td key={`total-${dayIndex}`} rowSpan={3}>
                    no schedule
                  </td>
                );
              }
            }

            return (
              <React.Fragment key={`day-${dayIndex}-${rowIndex}`}>
                {timeCell}
                {rowIndex === 0 && totalHoursCell}
              </React.Fragment>
            );
          })}
          {rowIndex === 0 && <td rowSpan={3}>{totalWeekHours} h</td>}
        </tr>
      ))}

      {/* Modal za uređivanje */}
      {isModalOpen && (
        <EditScheduleModal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          person={person}
          schedule={personSchedule.schedule}
          daysOfWeek={daysOfWeek}
          onSave={handleSaveSchedule}
        />
      )}
    </>
  );
}

export default RasporedRow;
